import { useState } from 'react';
import { useSnackbar } from 'notistack';

import Container from '@mui/material/Container';

import { useAuthContext } from 'src/auth/hooks';
import { createPortalSession } from 'src/redux/slices/subscription';

import { useSettingsContext } from '../settings';
import AnnouncementBanner from '../announcement-banner';

// ----------------------------------------------------------------------

/**
 * Tells a seller their payment did not go through, while they can still do something about it.
 *
 * A failed card leaves the seller on 'past_due', which keeps full access while Stripe retries. Up
 * to now the app said nothing at all: they carried on working, unaware, and the first they knew
 * was the day access stopped. Stripe emails them, but an email can be missed and this cannot,
 * because it sits on every page for as long as the payment is outstanding.
 *
 * Not dismissable on purpose. The one thing it asks for is the one thing that keeps their account,
 * and it disappears by itself the moment the payment goes through.
 */
export default function PastDueBanner () {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const settings = useSettingsContext();
  const [loading, setLoading] = useState(false);

  const handleUpdateCard = async () => {
    try {
      setLoading(true);
      // Stripe's own hosted page: the card is entered there, never here.
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (error) {
      // Only reached if the redirect never happens, so the button has to become usable again.
      setLoading(false);
      enqueueSnackbar("Couldn't open the payment page. Try again.", { variant: 'error' });
    }
  };

  // Nothing at all for everyone else: this renders above every page, so it must leave no trace
  // when there is no unpaid payment to report.
  if (user?.status !== 'past_due') return null;

  // Matched to the width pages use, so it lines up with the content beneath it.
  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <AnnouncementBanner
        storageKey="billing-past-due"
        severity="warning"
        dismissable={false}
        title="Payment needs your attention"
        message="Your last payment didn't go through. Update your card so you can keep using Voolist."
        action={{ label: 'Update card', onClick: handleUpdateCard, loading }}
      />
    </Container>
  );
}
